import React, { useState } from "react";
import { auth } from "../db/firebase";
import { signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowLeft } from "react-icons/hi2";
import { resolveUserAccess } from "../utils/resolveUserAccess";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
const labelClass =
  "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as { from?: string } | null)?.from;

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setResetSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetEmailSent(true);
    } catch (err: any) {
      setError(
        err.code === "auth/user-not-found"
          ? (t("noAccountWithEmail") || "No account found with this email address.")
          : err.message || (t("error") || "An error occurred")
      );
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const { approved } = await resolveUserAccess(userCredential.user.uid);

      if (approved) {
        navigate(redirectTo || "/dashboard", { replace: true });
      } else {
        await signOut(auth);
        setError(t("accountNotApproved"));
      }
    } catch (err: any) {
      setError(err.message || t("error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-[#5932EA]/5 dark:from-gray-900 dark:via-gray-900 dark:to-[#5932EA]/10 p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-md">
        <div className="rounded-2xl shadow-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-8 sm:px-8 sm:py-10 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80">
            <div className="flex items-center gap-3">
              <img
                src="/imgs/logo-192x192.png"
                alt="Praisify"
                width={64}
                height={64}
                className="w-16 h-16 rounded-2xl shadow-sm shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                  {t("login")}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {t("signInToContinue") || "Sign in to continue"}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 sm:p-8">
            {showForgotPassword ? (
              <form onSubmit={handleForgotPassword}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t("forgotPassword") || "Forgot password"}
                  </span>
                </div>

                {resetEmailSent ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t("checkEmailForReset") ||
                        "Check your email for a link to reset your password."}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetEmailSent(false);
                        setError("");
                      }}
                      className="flex items-center gap-2 text-sm text-[#5932EA] hover:text-[#4a28c7] font-medium"
                    >
                      <HiOutlineArrowLeft className="h-4 w-4" />
                      {t("backToLogin") || "Back to login"}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-5">
                      <div>
                        <label htmlFor="reset-email" className={labelClass}>
                          {t("email")}
                        </label>
                        <input
                          maxLength={100}
                          type="email"
                          id="reset-email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t("email")}
                          required
                          className={inputBase}
                        />
                      </div>
                    </div>

                    {error && (
                      <p className="mt-4 text-sm text-red-500 dark:text-red-400 text-center bg-red-50 dark:bg-red-900/20 py-2 px-3 rounded-xl">
                        {error}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setError("");
                      }}
                      className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                    >
                        <HiOutlineArrowLeft className="h-4 w-4" />
                        {t("back") || "Back"}
                      </button>
                      <button
                        type="submit"
                        disabled={resetSubmitting}
                        className="flex-1 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        {resetSubmitting
                          ? t("sending") || "Sending..."
                          : t("sendResetLink") || "Send reset link"}
                      </button>
                    </div>
                  </>
                )}
              </form>
            ) : (
              <form onSubmit={handleLogin}>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t("credentials") || "Credentials"}
                  </span>
                </div>

                <div className="space-y-5">
                  <div>
                    <label htmlFor="email" className={labelClass}>
                      {t("email")}
                    </label>
                    <input
                      maxLength={100}
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("email")}
                      required
                      className={inputBase}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className={labelClass}>
                      {t("password")}
                    </label>
                    <input
                      maxLength={100}
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t("password")}
                      required
                      className={inputBase}
                    />
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="mt-2 text-sm text-[#5932EA] hover:text-[#4a28c7] font-medium"
                    >
                      {t("forgotPassword") || "Forgot password?"}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="mt-4 text-sm text-red-500 dark:text-red-400 text-center bg-red-50 dark:bg-red-900/20 py-2 px-3 rounded-xl">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-6 py-3 bg-[#5932EA] text-white font-semibold rounded-xl hover:bg-[#4a28c7] disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {isSubmitting ? t("loading") || "Loading..." : t("loginButton")}
                </button>
              </form>
            )}
          </div>

          {/* Footer link */}
          <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              <Link
                to="/organization-signup"
                className="text-[#5932EA] hover:text-[#4a28c7] font-medium transition-colors"
              >
                {t("registerOrganization")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
